import React, { SyntheticEvent } from 'react';
import { Box, Typography } from '@mui/material';
import RoleTypeTag from '../../components/RoleTypeTag';
import { CommonStyle } from '../../../../utils/muiStyle';
import MySkeleton from '../../../../components/Skeleton';
// import { useNavigate } from 'react-router';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import chatIcon from '../../../../assets/images/chat_icon_3.svg';
import { alphaColorsNew } from '../../../../utils/alphabates-colors';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

interface UserCardProps {
  mem?: any;
  bio?: string;
  loading?: boolean;
  imageUrl?: string;
  category?: string;
  cardWidth?: string;
  cardHeight?: string;
  displayName?: string;
  communityMembers?: any;
  handleOnSelectMember?:any;
  allCommunityMembersList?: any;
}

const UserCard: React.FC<UserCardProps> = ({
  mem,
  bio,
  loading,
  imageUrl,
  category,
  cardWidth = '250px',
  cardHeight = '250px',
  displayName,
  handleOnSelectMember
}) => {

  const firstLetter = displayName?.charAt(0)?.toUpperCase() || '';
  const bgColor = alphaColorsNew[firstLetter as keyof typeof alphaColorsNew] || '#444444';
  const hasImage = imageUrl && imageUrl.trim() !== '';

  return (
    <Box
      sx={{
        width: cardWidth,
        height: cardHeight,
        p: '12px',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: '5px',
        cursor: loading ? 'default' : 'pointer',
        '&:hover .user-card-gray-overlay': { opacity: 0 },
        // Phone: a card sized by its content (Community passes height 'auto') keeps the
        // portrait proportion as a floor and grows when a name or bio needs more lines.
        [MOBILE_SX]: cardHeight === 'auto' ? { minHeight: 236 } : {}
      }}
      onClick={() => !loading && handleOnSelectMember(mem)}
    >
      {/* BACKGROUND IMAGE */}
      {!loading && hasImage && (
        <img
          src={imageUrl}
          alt={displayName}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            objectFit: 'cover'
          }}
        />
      )}
      {!loading && !hasImage && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            bgcolor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ fontSize: '72px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1, userSelect: 'none' }}>
            {firstLetter}
          </Typography>
        </Box>
      )}

      {/* GRADIENT */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.60) 95%)'
        }}
      />

      {/* GRAY OVERLAY - fades out on hover */}
      {!loading && (
        <Box
          className="user-card-gray-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.18)',
            borderRadius: '6px',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 1,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* CATEGORY PILL */}
      {!loading && (
        <Box
          sx={{
            px: '8px',
            py: '5px',
            background: '#444444',
            borderRadius: '20px',
            zIndex: 2,
            display: 'inline-flex',
            alignSelf: 'flex-start'
          }}
        >
          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: '12px',
              fontWeight: 500,
              textTransform: 'uppercase',
              lineHeight: '14px'
            }}
          >
            {category || 'Member'}
          </Typography>
        </Box>
      )}

      {/* BOTTOM CONTENT */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          zIndex: 2
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '16px',
              // Phone: a long name wraps onto a second line instead of running out of the card.
              [MOBILE_SX]: { lineHeight: 1.2, overflowWrap: 'anywhere' }
            }}
          >
            {displayName}
          </Typography>

          <Typography
            sx={{
              color: '#FDFDFD',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              minHeight: '48px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // Phone: the whole bio, no clamp or ellipsis; the card grows to fit it.
              [MOBILE_SX]: {
                fontSize: '13px',
                lineHeight: '17px',
                minHeight: 0,
                display: 'block',
                WebkitLineClamp: 'unset',
                overflow: 'visible',
                textOverflow: 'clip',
                overflowWrap: 'anywhere'
              }
            }}
          >
            {bio || '\u00A0'}
          </Typography>
        </Box>

        {/* CTA BUTTON WITH ICON */}
        <DosenButton
          tone="cta"
          sizeVariant="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleOnSelectMember(mem);
          }}
          sx={{
            minWidth: 40,
            width: 40,
            height: 40,
            p: 0,
            borderRadius: '50%',
            // Phone: the card is narrow, so the button sits in the top corner and the name gets the full width.
            [MOBILE_SX]: { position: 'absolute', top: 10, right: 10 }
          }}
        >
          <img src={chatIcon} alt="Chat" style={{ width: 22, height: 22 }} />
        </DosenButton>
      </Box>
    </Box>
  );
};


export default UserCard;
